import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { format } from "date-fns";
import { LogOut, ShieldCheck, Smartphone, Trash2 } from "lucide-react";

interface DeviceSession {
  session_id: string;
  country_code: string;
  verified_at: string;
  expires_at: string;
}

interface Factor {
  id: string;
  friendly_name: string | null;
  status: string;
}

interface BackupEnrollment {
  factorId: string;
  qrCode: string;
  secret: string;
}

const AdminAccountSecurity = () => {
  const [sessions, setSessions] = useState<DeviceSession[]>([]);
  const [factors, setFactors] = useState<Factor[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [backup, setBackup] = useState<BackupEnrollment | null>(null);
  const [backupCode, setBackupCode] = useState("");

  const load = useCallback(async () => {
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData.session?.access_token ?? "";
    try {
      const payload = JSON.parse(atob(accessToken.split(".")[1] ?? "")) as { session_id?: string };
      setCurrentSessionId(payload.session_id ?? "");
    } catch {
      setCurrentSessionId("");
    }

    const [{ data: rows }, { data: factorData }] = await Promise.all([
      supabase
        .from("admin_access_sessions")
        .select("session_id, country_code, verified_at, expires_at")
        .order("verified_at", { ascending: false }),
      supabase.auth.mfa.listFactors(),
    ]);
    setSessions((rows ?? []) as DeviceSession[]);
    setFactors((factorData?.totp ?? []) as unknown as Factor[]);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const revokeSession = async (sessionId: string) => {
    const { error } = await supabase.from("admin_access_sessions").delete().eq("session_id", sessionId);
    if (error) {
      toast.error("Gerätefreigabe konnte nicht widerrufen werden.");
      return;
    }
    toast.success("Gerätefreigabe widerrufen.");
    if (sessionId === currentSessionId) {
      await supabase.auth.signOut();
      return;
    }
    await load();
  };

  const signOutEverywhere = async () => {
    setBusy(true);
    try {
      await supabase.from("admin_access_sessions").delete().neq("session_id", "");
      await supabase.auth.signOut({ scope: "global" });
    } finally {
      setBusy(false);
    }
  };

  const startBackupEnrollment = async () => {
    setBusy(true);
    try {
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: "totp",
        friendlyName: `Ersatzgerät ${format(new Date(), "dd.MM.yyyy HH:mm")}`,
      });
      if (error) {
        toast.error(error.message);
        return;
      }
      setBackup({ factorId: data.id, qrCode: data.totp.qr_code, secret: data.totp.secret });
    } finally {
      setBusy(false);
    }
  };

  const confirmBackup = async () => {
    if (!backup || !/^\d{6}$/.test(backupCode)) {
      toast.error("Bitte geben Sie den sechsstelligen Code ein.");
      return;
    }
    setBusy(true);
    try {
      const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({ factorId: backup.factorId });
      if (challengeError) throw challengeError;
      const { error } = await supabase.auth.mfa.verify({ factorId: backup.factorId, challengeId: challenge.id, code: backupCode });
      if (error) throw error;
      toast.success("Ersatzgerät wurde hinterlegt.");
      setBackup(null);
      setBackupCode("");
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Der Code konnte nicht bestätigt werden.");
    } finally {
      setBusy(false);
    }
  };

  const removeFactor = async (factorId: string) => {
    const { error } = await supabase.auth.mfa.unenroll({ factorId });
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Authenticator entfernt.");
    await load();
  };

  return (
    <>
      <Helmet>
        <title>Sicherheit | Leistungsnachweise</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <h1 className="mb-6 text-2xl font-semibold tracking-tight">Sicherheit</h1>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Smartphone className="h-4 w-4" /> Freigegebene Geräte
          </CardTitle>
          <CardDescription>
            Auf diesen Geräten ist der Adminzugang ohne erneuten Code gültig. Widerrufen Sie Geräte, die Sie nicht mehr nutzen.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {loading ? (
            <Skeleton className="h-16 w-full" />
          ) : sessions.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aktuell ist kein Gerät dauerhaft freigegeben.</p>
          ) : (
            sessions.map((s) => (
              <div key={s.session_id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3">
                <div className="text-sm">
                  <p className="font-medium">
                    {s.session_id === currentSessionId ? "Dieses Gerät" : "Gerät"} · Land {s.country_code}
                  </p>
                  <p className="text-muted-foreground">
                    Freigegeben am {format(new Date(s.verified_at), "dd.MM.yyyy 'um' HH:mm")} · gültig bis{" "}
                    {format(new Date(s.expires_at), "dd.MM.yyyy")}
                  </p>
                </div>
                <Button variant="outline" size="sm" className="gap-2" onClick={() => revokeSession(s.session_id)}>
                  <Trash2 className="h-4 w-4" /> Widerrufen
                </Button>
              </div>
            ))
          )}
          <Button variant="destructive" className="gap-2" onClick={signOutEverywhere} disabled={busy}>
            <LogOut className="h-4 w-4" /> Auf allen Geräten abmelden
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="h-4 w-4" /> Authenticator-Apps
          </CardTitle>
          <CardDescription>
            Hinterlegen Sie ein zweites Gerät als Ersatz. So kommen Sie auch dann in den Adminbereich, wenn das erste Gerät verloren geht.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <Skeleton className="h-16 w-full" />
          ) : (
            factors.map((f) => (
              <div key={f.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3 text-sm">
                <div>
                  <p className="font-medium">{f.friendly_name ?? "Authenticator"}</p>
                  <p className="text-muted-foreground">Status: {f.status === "verified" ? "aktiv" : "nicht bestätigt"}</p>
                </div>
                {factors.filter((x) => x.status === "verified").length > 1 && (
                  <Button variant="outline" size="sm" className="gap-2" onClick={() => removeFactor(f.id)}>
                    <Trash2 className="h-4 w-4" /> Entfernen
                  </Button>
                )}
              </div>
            ))
          )}

          {backup ? (
            <div className="space-y-4 rounded-lg border p-4">
              <img src={backup.qrCode} alt="QR-Code für das Ersatzgerät" className="mx-auto h-44 w-44 rounded-md border bg-background p-2" />
              <div>
                <Label>Schlüssel für manuelle Eingabe</Label>
                <p className="mt-1 break-all rounded-md bg-muted p-3 font-mono text-sm">{backup.secret}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="backup-code">Sechsstelliger Code</Label>
                <Input
                  id="backup-code"
                  inputMode="numeric"
                  maxLength={6}
                  value={backupCode}
                  onChange={(e) => setBackupCode(e.target.value.replace(/\D/g, ""))}
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={confirmBackup} disabled={busy || backupCode.length !== 6}>Ersatzgerät bestätigen</Button>
                <Button variant="outline" onClick={() => { setBackup(null); setBackupCode(""); }}>Abbrechen</Button>
              </div>
            </div>
          ) : (
            <Button variant="outline" onClick={startBackupEnrollment} disabled={busy}>
              Ersatzgerät hinzufügen
            </Button>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default AdminAccountSecurity;
